<template>
  <main
    id="main-content"
    tabindex="-1"
    class="flex h-dvh justify-center overflow-y-auto bg-muted px-4 py-10 text-foreground outline-none md:py-14"
    data-test-id="platform-create"
  >
    <div class="flex w-full max-w-5xl flex-wrap items-start gap-8">
      <section class="flex min-w-0 flex-[1_1_21.25rem] flex-col gap-4 md:pt-6">
        <span class="text-xs font-semibold tracking-[0.08em] text-primary uppercase">
          {{ t('admin.setup.create.eyebrow') }}
        </span>
        <h1 class="font-display text-3xl leading-tight font-semibold tracking-tight md:text-4xl">
          {{ t('admin.setup.create.title') }}
        </h1>
        <p class="text-base leading-relaxed text-muted-foreground md:text-lg">
          {{ t('admin.setup.create.intro') }}
        </p>
        <ol class="mt-2 flex flex-col gap-2.5">
          <li v-for="(stepKey, index) in STEPS" :key="stepKey" class="flex items-center gap-2.5 text-sm">
            <span
              class="flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
              :class="index === 0 ? 'bg-primary text-primary-foreground' : 'border-2 border-border bg-card'"
              aria-hidden="true"
            >
              {{ index + 1 }}
            </span>
            <span :class="index === 0 ? 'font-semibold' : 'text-muted-foreground'">
              {{ t(`admin.setup.create.steps.${stepKey}`) }}
            </span>
          </li>
        </ol>
      </section>

      <section
        aria-labelledby="platform-create-form-title"
        class="flex min-w-0 flex-[1_1_26.25rem] flex-col gap-5 rounded-lg border bg-card p-6 md:p-8"
      >
        <h2 id="platform-create-form-title" class="text-lg font-semibold">{{ t('admin.setup.create.formTitle') }}</h2>
        <form class="flex flex-col gap-5" novalidate @submit.prevent="submit">
          <div class="flex flex-col gap-2">
            <Label for="create-organization">{{ t('admin.setup.create.organization.label') }}</Label>
            <Input
              id="create-organization"
              v-model="form.organizationName"
              autocomplete="organization"
              required
              :placeholder="t('admin.setup.create.organization.placeholder')"
              aria-describedby="create-organization-description"
              data-test-id="platform-create-organization"
            />
            <p id="create-organization-description" class="text-sm text-muted-foreground">
              {{ t('admin.setup.create.organization.description') }}
            </p>
          </div>
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div class="flex flex-col gap-2">
              <Label for="create-firstname">{{ t('admin.setup.create.firstname') }}</Label>
              <Input
                id="create-firstname"
                v-model="form.firstname"
                autocomplete="given-name"
                required
                data-test-id="platform-create-firstname"
              />
            </div>
            <div class="flex flex-col gap-2">
              <Label for="create-lastname">{{ t('admin.setup.create.lastname') }}</Label>
              <Input
                id="create-lastname"
                v-model="form.lastname"
                autocomplete="family-name"
                required
                data-test-id="platform-create-lastname"
              />
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <Label for="create-email">{{ t('admin.setup.create.email') }}</Label>
            <Input
              id="create-email"
              v-model="form.email"
              type="email"
              autocomplete="username"
              required
              data-test-id="platform-create-email"
            />
          </div>
          <div class="flex flex-col gap-2">
            <Label for="create-password">{{ t('admin.setup.create.password.label') }}</Label>
            <Input
              id="create-password"
              v-model="form.password"
              type="password"
              autocomplete="new-password"
              required
              aria-describedby="create-password-description"
              data-test-id="platform-create-password"
            />
            <p id="create-password-description" class="text-sm text-muted-foreground">
              {{ t('admin.setup.create.password.description') }}
            </p>
          </div>

          <p v-if="errorRef" role="alert" class="text-sm text-destructive" data-test-id="platform-create-error">
            {{ errorRef }}
            <router-link v-if="alreadyCreatedRef" :to="ROUTES.INDEX" class="font-medium">
              {{ t('admin.setup.create.errors.goToLogin') }}
            </router-link>
          </p>

          <Button
            type="submit"
            size="lg"
            class="w-full"
            :disabled="isSubmittingRef"
            data-test-id="platform-create-submit"
          >
            <Spinner v-if="isSubmittingRef" aria-hidden="true" />
            {{ t('admin.setup.create.submit') }}
          </Button>
        </form>
        <p class="text-xs leading-relaxed text-muted-foreground">{{ t('admin.setup.create.note') }}</p>
      </section>
    </div>
  </main>
</template>

<script setup lang="ts">
import { reactive, ref, watch } from 'vue'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { createFirstAdmin } from '@/features/admin/platform-setup/api'
import authStore from '@/features/auth/stores/auth/auth.store'
import useRouter from '@/hooks/use-router.hook'
import useWhiteLabel from '@/hooks/use-white-label.hook'
import { useI18n } from '@/lib/i18n'
import { EMAIL_REGEX, ROUTES } from '@/utils/config/constants'

const STEPS = ['account', 'settings', 'program'] as const
// Même règle que l'API (api/utils/passwordValidation.ts).
const PASSWORD_RULE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@$!%*?&\-_#]).{8,}$/

const { t } = useI18n()
const router = useRouter()
const { whiteLabelRef, isPendingRef } = useWhiteLabel()

const form = reactive({ organizationName: '', firstname: '', lastname: '', email: '', password: '' })
const errorRef = ref('')
const alreadyCreatedRef = ref(false)
const isSubmittingRef = ref(false)

// Plateforme déjà créée (ou ancienne plateforme) : cet écran n'a plus lieu d'être.
watch(
  [isPendingRef, () => whiteLabelRef.value.setupNeedsFirstAdmin],
  ([isPending, needsFirstAdmin]) => {
    if (!isPending && !needsFirstAdmin && !isSubmittingRef.value) {
      router?.replace(ROUTES.INDEX).catch(() => {})
    }
  },
  { immediate: true },
)

function validate(): string {
  if (!form.organizationName.trim() || !form.firstname.trim() || !form.lastname.trim()) {
    return t('admin.setup.create.errors.required')
  }
  if (!EMAIL_REGEX.test(form.email.trim())) return t('admin.setup.create.errors.email')
  if (!PASSWORD_RULE.test(form.password)) return t('admin.setup.create.errors.password')
  return ''
}

async function submit() {
  alreadyCreatedRef.value = false
  errorRef.value = validate()
  if (errorRef.value) return
  isSubmittingRef.value = true
  try {
    const { token } = await createFirstAdmin({ ...form, email: form.email.trim() })
    await authStore.setAuthToken(token)
    // /redirect envoie l'admin sur le tableau de bord, qui ouvre l'assistant (statut « pending »).
    window.location.assign(ROUTES.REDIRECT)
  } catch (error: any) {
    isSubmittingRef.value = false
    alreadyCreatedRef.value = error?.status === 409
    errorRef.value = alreadyCreatedRef.value
      ? t('admin.setup.create.errors.alreadyCreated')
      : t('admin.setup.create.errors.generic')
  }
}
</script>
